import { FormAggregatedResultElementProps } from "@/components/form-result/aggregated-result/components/types.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { ApiFormElementWriteToFieldTargetType } from "@/types/forms/form-edit.types.ts";
import { LinkTo, Stack, Text, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export const FormAggregatedResultWriteToFieldElement = ({
  element,
  result,
}: FormAggregatedResultElementProps) => {
  const { t } = useTranslation();
  const { id: surveyId } = useParams();
  const { getFieldTitle } = useCustomFieldsHook();
  const settings = element.write_to_field_settings;

  const targetTitle = useMemo(() => {
    if (!settings) return "";

    if (settings.target_type === ApiFormElementWriteToFieldTargetType.field) {
      const field = element.fields?.find((field) => field.id === settings.field_id);
      return field ? getFieldTitle(field) : "";
    }

    const table = element.tables?.find((table) => table.id === settings.table_id);
    return table?.title ?? "";
  }, [element.fields, element.tables, getFieldTitle, settings]);

  if (!settings) return null;

  const updatedProfilesCount = result?.write_to_field_data?.total_answers ?? 0;
  const participantsLink = surveyId ? `/surveys/${surveyId}/view/participants` : "/surveys";

  return (
    <Stack gap={"md"}>
      <Title
        header={settings.title || t("surveys:write_to_field_title")}
        size={"sm"}
        required={settings.is_required}
        caption={t("surveys:write_to_field_results_destination", {
          target: targetTitle,
        })}
      />
      {updatedProfilesCount > 0 && (
        <Title
          size={"lg"}
          header={t("plurals:employee_profiles_updated", { count: updatedProfilesCount })}
          captionSlot={
            <Text className={"text-secondary text-lg"}>
              {t("surveys:write_to_field_view_responses_before_link")}{" "}
              <LinkTo to={participantsLink}>{t("common:participants")}</LinkTo>{" "}
              {t("surveys:write_to_field_view_responses_after_link")}
            </Text>
          }
        />
      )}
    </Stack>
  );
};
